import { useEffect, useState } from 'react';

/**
 * Where the visible part of the page is, in layout-viewport coordinates.
 *
 * WebKit does not shrink the page for the keyboard: it scrolls the layout
 * viewport up to the focused field. Anything `position: fixed` keeps its
 * layout coordinates and so leaves the screen — the chat header went under
 * the status bar, an incoming-call banner would be above the top edge
 * (iPhone, 2026-09-02). Android's WebView, once the container really shrinks
 * (MainActivity applies the keyboard inset), reports offsetTop 0 and a
 * height equal to innerHeight, so on Android this box is simply the page.
 *
 * `top`/`height` are what a full-screen fixed element should use while the
 * keyboard is up; `keyboard` is the height the keyboard takes.
 */
export function useVisualViewportBox() {
  const [box, setBox] = useState({ top: 0, height: null, keyboard: 0 });
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const keyboard = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        const shrunk = keyboard > 100 || vv.offsetTop > 0;
        setBox({ top: shrunk ? vv.offsetTop : 0, height: shrunk ? vv.height : null, keyboard });
      });
    };
    vv.addEventListener('resize', measure);
    vv.addEventListener('scroll', measure);
    measure();
    return () => { cancelAnimationFrame(raf); vv.removeEventListener('resize', measure); vv.removeEventListener('scroll', measure); };
  }, []);
  return box;
}
